"use client"

import {useState, useEffect, FC, useCallback} from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  Bell,
  Users,
  Briefcase,
  Plus,
  Trash2,
  CircleUserRound,
} from "lucide-react"
import { ReminderConfiguration, TemplateConfiguration } from "@/models/interface"
import { EditReminderDialog } from "./dialogs/edit-reminder-dialog"
import { useNotificationCenterApi } from "@/hooks"
import { useSubjectSelector } from "@/common"
import { notificationStore } from "@/store/notification-store"
import { CreateReminderDialog } from "./dialogs/create-reminder-dialog"
import { EditMessageContext, PreferredCultureId, PreferredLanguage, RecipientType } from "@/models/enum"
import { EditMessageModal } from "./dialogs/edit-message-modal"
import useTranslation from "@/hooks/use-translation"
import { getReminderDisplayLabel } from "@/lib/get-timesheet-labels"
import { toast } from "@/hooks/use-toast"

export const NotificationCenter: FC = () => {
  const { t, setLanguage } = useTranslation()
  const [filteredReminderConfigs, setFilteredReminderConfigs] = useState<ReminderConfiguration[]>([])
  const [deleteReminderId, setDeleteReminderId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [addReminderOpen, setAddReminderOpen] = useState(false)
  const [editReminderOpen, setEditReminderOpen] = useState(false)
  const [editingReminderConfig, setEditingReminderConfig] = useState<ReminderConfiguration | null>(null)

  const [editMessageOpen, setEditMessageOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<TemplateConfiguration | null>(null)
  const [editMessageContext, setEditMessageContext] = useState<EditMessageContext>(EditMessageContext.Edit)
  const [isSavingTemplate, setIsSavingTemplate] = useState(false)

  const { selectedRecipientType } = useSubjectSelector(notificationStore, ['selectedRecipientType'])

  const { fetchReminderConfigurations, updateReminderConfiguration, updateBulkReminderConfiguration, fetchUserInfo, deleteReminderConfiguration } = useNotificationCenterApi()

  // Compute if all reminders are enabled to determine switch state
  const allRemindersEnabled = filteredReminderConfigs.length > 0 
    && filteredReminderConfigs.some(config => config.Data.ReminderEnabled)

  const refetchReminders = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await fetchReminderConfigurations()
      const filteredReminderConfigs = response.filter((item) => item.Data.RecipientType === selectedRecipientType)
      setFilteredReminderConfigs(filteredReminderConfigs)
    } catch (error) {
      console.error('Error fetching reminder configurations:', error)
    } finally {
      setIsLoading(false)
    }
  }, [fetchReminderConfigurations, selectedRecipientType])

  useEffect(() => {
    refetchReminders()
  }, [selectedRecipientType])

  // Fetch user preferences and set language
  useEffect(() => {
    const setUserPreferredLanguage = async () => {
      try {
        const userInfo = await fetchUserInfo()
        
        let preferredLanguage: PreferredLanguage = PreferredLanguage.English
        
        if (userInfo.PreferredCultureId === PreferredCultureId.French) {
          preferredLanguage = PreferredLanguage.French
        } else if (userInfo.PreferredCultureId === PreferredCultureId.English) {
          preferredLanguage = PreferredLanguage.English
        }
        
        setLanguage(preferredLanguage)
      } catch (error) {
        console.error('Error fetching user preferences, keeping default language:', error)
      }
    }

    setUserPreferredLanguage()
  }, [])

  const addReminder = () => {
    setAddReminderOpen(true)
  }

  const updateReminder = useCallback(async (enabled: boolean, reminderConfig: ReminderConfiguration) => {
    try {
      // Create updated reminder with enabled/disabled state
      const updatedReminder = {
        ...reminderConfig.Data,
        ReminderEnabled: enabled
      }

      // Create updated reminder configuration
      const updatedReminderConfig = {
        ...reminderConfig,
        Data: updatedReminder
      }

      // Save via API
      await updateReminderConfiguration(updatedReminderConfig)
      
      // Refresh the list
      await refetchReminders()
    } catch (error) {
      console.error('Error updating reminder enabled state:', error)
    }
  }, [refetchReminders, updateReminderConfiguration])

  const editReminder = useCallback((reminderConfig: ReminderConfiguration) => {
    setEditingReminderConfig({ ...reminderConfig })
    setEditReminderOpen(true)
  }, [])

  const handleEditMessage = useCallback((template: TemplateConfiguration, context: EditMessageContext) => {
    setEditingTemplate(template)
    setEditMessageContext(context)
    setEditMessageOpen(true)
  }, [])

  const deleteReminderConfig = async (reminderId: string) => {
    try {
      // Find the reminder configuration by ID
      const reminderConfig = filteredReminderConfigs.find(config => config.id === reminderId)
      if (!reminderConfig) {
        console.error('Reminder configuration not found:', reminderId)
        return
      }

      await deleteReminderConfiguration(reminderConfig)
      
      // Refresh the list to show updated state
      await refetchReminders()
    } catch (error) {
      console.error('Error deleting reminder configuration:', error)
    }
  }

  const handleOpenEditMessage = useCallback((open: boolean) => {
    setEditMessageOpen(open)
    if (!open) {
      setEditingTemplate(null)
    }
  }, [])

  return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t("dashboard.header.title")}</h1>
              <p className="text-gray-600 mt-1">{t("dashboard.header.description")}</p>
            </div>
          </div>

          {/* Horizontal Navigation */}
          <div className="flex items-center justify-center">
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
              <Button
                  variant={selectedRecipientType === RecipientType.WORKER ? "default" : "ghost"}
                  className="flex items-center gap-2 px-6 py-2"
                  onClick={() => {
                    notificationStore.setSelectedRecipientType(RecipientType.WORKER)
                  }}
              >
                <Users className="w-4 h-4" />
                {t("dashboard.navigation.workers")}
              </Button>
              <Button
                  variant={selectedRecipientType === RecipientType.CLIENT ? "default" : "ghost"}
                  className="flex items-center gap-2 px-6 py-2"
                  onClick={() => {
                    notificationStore.setSelectedRecipientType(RecipientType.CLIENT)
                  }}
              >
                <Briefcase className="w-4 h-4" />
                {t("dashboard.navigation.clients")}
              </Button>
              <Button
                  variant={selectedRecipientType === RecipientType.INTERNAL ? "default" : "ghost"}
                  className="flex items-center gap-2 px-6 py-2"
                  onClick={() => {
                    notificationStore.setSelectedRecipientType(RecipientType.INTERNAL)
                  }}
              >
                <CircleUserRound className="w-4 h-4" />
                {t("dashboard.navigation.internal")}
              </Button>
            </div>
          </div>

          {/* Main Configuration Panel */}
          <Card className="max-h-[70vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {t(`dashboard.panel.title.${selectedRecipientType}`, {
                      count: filteredReminderConfigs.length
                    })}
                  </CardTitle>
                </div>
                <div className="flex items-center gap-4">
                  <Switch
                      checked={allRemindersEnabled}
                      disabled={!filteredReminderConfigs.length}
                      onCheckedChange={async (enabled) => {
                        try {
                          setIsLoading(true)
                          // Update all reminders with the new enabled state
                          const updatedConfigs: ReminderConfiguration[] = filteredReminderConfigs.map(config => ({
                            ...config,
                            Data: {
                              ...config.Data,
                              ReminderEnabled: enabled
                            }
                          }))
                          
                          await updateBulkReminderConfiguration(updatedConfigs)
                          await refetchReminders()
                        } catch (error) {
                          console.error('Error updating bulk reminder configurations:', error)
                          toast({
                            description: 'Unknown error',
                            variant: "destructive"
                          })
                        } finally {
                          setIsLoading(false)
                        }
                      }}
                  />
                  <Button onClick={addReminder} size="sm" className="bg-slate-800 hover:bg-slate-700">
                    <Plus className="w-4 h-4 mr-1" />
                    {t("dashboard.panel.addReminder")}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {(
                  <>
                    {filteredReminderConfigs.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>{t("dashboard.panel.noRemindersConfigured")}</p>
                          <p className="text-sm">{t("dashboard.panel.clickAddReminderToStart")}</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                          {filteredReminderConfigs.map((reminderConfig, index) => {
                            const reminder = reminderConfig.Data
                            return (
                              <div
                                  key={`${reminderConfig.id || index}`}
                                  className="flex items-center justify-between p-4 bg-white rounded-lg border hover:bg-gray-50 cursor-pointer transition-colors"
                                  onClick={() => editReminder(reminderConfig)}
                              >
                                <div className="flex items-center gap-4">
                                  <Switch
                                      checked={reminder.ReminderEnabled}
                                      onCheckedChange={(enabled) => {
                                        updateReminder(enabled, reminderConfig)
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                  />
                                  <span className="font-medium text-gray-900">{reminder.ReminderTitle}</span>
                                </div>

                                <div className="flex items-center gap-6">
                                  <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Users className="w-4 h-4" />
                                    <span>
                                      {getReminderDisplayLabel(reminder, t)}
                                    </span>
                                  </div>

                                  <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setDeleteReminderId(reminderConfig.id || null)
                                      }}
                                      className="text-gray-400 hover:text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                    )}
                  </>
              )}
            </CardContent>
          </Card>

          {/* Create Reminder Modal */}
          <CreateReminderDialog 
            open={addReminderOpen} 
            setAddReminderOpen={setAddReminderOpen} 
            onEditMessage={handleEditMessage} 
            onSaveSuccess={refetchReminders}
          />

          {/* Edit Reminder Modal */}
          <EditReminderDialog
            open={editReminderOpen}
            onOpenChange={setEditReminderOpen}
            editingReminderConfig={editingReminderConfig}
            setEditingReminderConfig={setEditingReminderConfig}
            remindersLoading={isLoading}
            onEditMessage={handleEditMessage}
            onSaveSuccess={refetchReminders}
          />

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteReminderId !== null} onOpenChange={() => setDeleteReminderId(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("dashboard.deleteDialog.title")}</DialogTitle>
                <DialogDescription>
                  {t("dashboard.deleteDialog.description")}
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setDeleteReminderId(null)}>
                  {t("dashboard.deleteDialog.cancel")}
                </Button>
                <Button
                    variant="destructive"
                    onClick={async () => {
                      if (deleteReminderId) {
                        await deleteReminderConfig(deleteReminderId)
                        setDeleteReminderId(null)
                      }
                    }}
                >
                  {t("dashboard.deleteDialog.deleteButton")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit Message Modal */}
          <EditMessageModal 
            editMessageOpen={editMessageOpen}
            onOpenChange={handleOpenEditMessage}
            isSavingTemplate={isSavingTemplate}
            editingTemplate={editingTemplate}
            setEditingTemplate={setEditingTemplate}
            editMessageContext={editMessageContext}
            setEditMessageOpen={setEditMessageOpen}
            setIsSavingTemplate={setIsSavingTemplate}
          />
        </div>
      </div>
  )
}
